import { PropsWithChildren, useCallback } from 'react';
import {
  AppointmentProposalOptionProduct,
  AppointmentProposalOptionProductCreate,
  AppointmentProposalOptionProductsContext,
  AppointmentProposalOptionProductsProvider,
  useAppointmentProposalOptionProducts,
} from '../../../data-access/appointmentProposalOptionProducts';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import { createOfflineUUID } from '../../rxdb/utils/rxDbOperations';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { useEntityReplication } from '../../rxdb/utils/useEntityReplication';

const buildOfflineProduct = (
  appointmentId: string,
  appointmentProposalId: string,
  appointmentProposalOptionId: string,
  create: AppointmentProposalOptionProductCreate
): AppointmentProposalOptionProduct => ({
  appointmentProposalOptionProductId: createOfflineUUID(),
  appointmentId,
  appointmentProposalId,
  appointmentProposalOptionId,
  entityType: 'APPOINTMENT_PROPOSAL_OPTION_PRODUCT',
  jobId: create.jobId,
  pricebookId: create.pricebookId,
  status: 'ACTIVE',
  externalIds: [],
  taxable: create.taxable ?? false,
  warranties: create.warranties ?? [],
  linkedItems: create.linkedItems ?? [],
  pricebookPrices: create.pricebookPrices ?? [],
  categoryIds: create.categoryIds ?? [],
  equipmentTypeIds: create.equipmentTypeIds ?? [],
  images: create.images ?? [],
  sortOrder: create.sortOrder ?? new Date().getTime(),
  defaultPriceType: create.defaultPriceType ?? 'STANDARD',
  brand: create.brand,
  model: create.model,
  currency: create.currency,
  description: create.description,
  groupingName: create.groupingName,
  itemGroupName: create.itemGroupName,
  itemGroupRootId: create.itemGroupRootId,
  name: create.name,
  price: create.price,
  type: create.type,
  quantity: create.quantity,
  durationBillingId: create.durationBillingId,
});

const OfflineAppointmentProposalOptionProductsOverride: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const context = useAppointmentProposalOptionProducts();
  const data = useOfflineDbData();
  const {
    proposalProducts,
    updateProduct,
    upsertProducts,
    selectProposalProducts,
    setProductsMutating,
    clearProductsMutating,
  } = context;

  const { initError } = useEntityReplication(
    'appointment_proposal_option_products',
    'airship-appointment-proposal-option-products'
  );

  const findProduct = useCallback(
    (
      appointmentProposalOptionId: string,
      appointmentProposalOptionProductId: string
    ) =>
      proposalProducts.find(
        (product) =>
          product.appointmentProposalOptionId === appointmentProposalOptionId &&
          product.appointmentProposalOptionProductId ===
            appointmentProposalOptionProductId
      ),
    [proposalProducts]
  );

  const appendProducts: typeof context.appendProducts = useCallback(
    (products) =>
      data.bulkUpsertSynced('appointment_proposal_option_products', products),
    [data]
  );

  const fetchAppointmentProposalProducts: typeof context.fetchAppointmentProposalProducts =
    useCallback(
      async (_appointmentId, appointmentProposalId) => {
        selectProposalProducts(appointmentProposalId);
        const count = await data
          .find('appointment_proposal_option_products', {
            selector: { appointmentProposalId },
          })
          .then(
            (docs) => docs.filter((doc) => doc.status !== 'DELETED').length
          );
        if (count === 0) {
          upsertProducts([]);
        }
        return [];
      },
      [data, selectProposalProducts, upsertProducts]
    );

  const createAppointmentProposalOptionProduct: typeof context.createAppointmentProposalOptionProduct =
    useCallback(
      (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        create
      ) => {
        setProductsMutating({ creatingIds: [create.pricebookId] });
        return data
          .insertSynced(
            'appointment_proposal_option_products',
            buildOfflineProduct(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              create
            ),
            0
          )
          .catch((error) => {
            clearProductsMutating({
              creatingIds: [create.pricebookId],
              error: 'We were unable to add the product. Please try again.',
            });
            throw error;
          });
      },
      [data, setProductsMutating, clearProductsMutating]
    );

  const createManyAppointmentProposalOptionProducts: typeof context.createManyAppointmentProposalOptionProducts =
    useCallback(
      (appointmentId, appointmentProposalId, payloads) => {
        const creatingIds = payloads.map(({ product }) => product.pricebookId);
        setProductsMutating({ creatingIds });
        return data
          .bulkInsertSynced(
            'appointment_proposal_option_products',
            payloads.map(({ appointmentProposalOptionId, product }) =>
              buildOfflineProduct(
                appointmentId,
                appointmentProposalId,
                appointmentProposalOptionId,
                product
              )
            ),
            0
          )
          .catch((error) => {
            clearProductsMutating({
              creatingIds,
              error: 'We were unable to add the products. Please try again.',
            });
            throw error;
          });
      },
      [data, setProductsMutating, clearProductsMutating]
    );

  const updateAppointmentProposalOptionProduct: typeof context.updateAppointmentProposalOptionProduct =
    useCallback(
      (
        _appointmentId,
        _appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionProductId,
        update
      ) => {
        const previous = findProduct(
          appointmentProposalOptionId,
          appointmentProposalOptionProductId
        );

        setProductsMutating({});

        const updatePayload = {
          ...update,
          offlineSyncErrors: null,
        };

        // Optimistic: drag and drop reordering holds no local state, so the
        // list bounces without this. Reverted below if the local write fails.
        updateProduct(
          appointmentProposalOptionId,
          appointmentProposalOptionProductId,
          updatePayload
        );

        return data
          .patchSynced(
            'appointment_proposal_option_products',
            appointmentProposalOptionProductId,
            updatePayload,
            0
          )
          .catch((error) => {
            previous &&
              updateProduct(
                appointmentProposalOptionId,
                appointmentProposalOptionProductId,
                previous
              );
            clearProductsMutating({
              error:
                'We were unable to save your product changes. Please try again.',
            });
            throw error;
          });
      },
      [
        data,
        findProduct,
        setProductsMutating,
        updateProduct,
        clearProductsMutating,
      ]
    );

  const deleteAppointmentProposalOptionProduct: typeof context.deleteAppointmentProposalOptionProduct =
    useCallback(
      async (
        _appointmentId,
        _appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionProductId
      ) => {
        const previous = findProduct(
          appointmentProposalOptionId,
          appointmentProposalOptionProductId
        );

        setProductsMutating({});

        const updatePayload = {
          status: 'DELETED' as const,
          offlineSyncErrors: null,
        };

        // Optimistic: removes the item from the list immediately. Reverted
        // below if the local write fails.
        updateProduct(
          appointmentProposalOptionId,
          appointmentProposalOptionProductId,
          updatePayload
        );

        await data
          .softDeleteSynced(
            'appointment_proposal_option_products',
            appointmentProposalOptionProductId,
            0
          )
          .catch((error) => {
            previous &&
              updateProduct(
                appointmentProposalOptionId,
                appointmentProposalOptionProductId,
                previous
              );
            clearProductsMutating({
              error: 'We were unable to remove the product. Please try again.',
            });
            throw error;
          });
      },
      [
        data,
        findProduct,
        setProductsMutating,
        clearProductsMutating,
        updateProduct,
      ]
    );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="appointment proposal option products"
      />
    );
  }

  return (
    <AppointmentProposalOptionProductsContext.Provider
      value={{
        ...context,
        appendProducts,
        fetchAppointmentProposalProducts,
        createAppointmentProposalOptionProduct,
        createManyAppointmentProposalOptionProducts,
        updateAppointmentProposalOptionProduct,
        deleteAppointmentProposalOptionProduct,
      }}
    >
      {children}
    </AppointmentProposalOptionProductsContext.Provider>
  );
};

export const OfflineAppointmentProposalOptionProductsProvider: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <AppointmentProposalOptionProductsProvider>
    <OfflineAppointmentProposalOptionProductsOverride>
      {children}
    </OfflineAppointmentProposalOptionProductsOverride>
  </AppointmentProposalOptionProductsProvider>
);
